import { useEffect, useRef, useCallback } from 'react';
import { useConnectionStore } from './stores/connection.store';
import { useSubscriptionStore } from './stores/subscription.store';
import { useMetricsStore } from './stores/metrics.store';
import type { WebSocketEventMap } from './types';
import type { WebSocketMessage } from './stores/metrics.store';

// Re-export hooks
export { useWebSocketReconnectSync } from './hooks/use-websocket-reconnect-sync';
export type {
  DataChangedEvent,
  InstantHandler,
} from './hooks/use-realtime-sync';

// Main WebSocket hook
export function useWebSocket() {
  const status = useConnectionStore((state) => state.status);
  const isConnected = useConnectionStore((state) => state.isConnected);
  const lastError = useConnectionStore((state) => state.lastError);
  const connect = useConnectionStore((state) => state.connect);
  const disconnect = useConnectionStore((state) => state.disconnect);
  const forceReconnect = useConnectionStore((state) => state.forceReconnect);

  return {
    status,
    isConnected,
    lastError,
    connect,
    disconnect,
    forceReconnect,
  };
}

// Hook for WebSocket events with automatic subscription management
export function useWebSocketEvent<T extends keyof WebSocketEventMap>(
  channel: string,
  event: T,
  callback: (data: WebSocketEventMap[T]) => void,
  options?: {
    enabled?: boolean;
    dependencies?: unknown[];
  }
) {
  const listen = useSubscriptionStore((state) => state.listen);
  const stopListening = useSubscriptionStore((state) => state.stopListening);
  const isConnected = useConnectionStore((state) => state.isConnected);

  const subscriptionIdRef = useRef<string | null>(null);
  const callbackRef = useRef(callback);

  // Update callback ref when it changes
  useEffect(() => {
    callbackRef.current = callback;
  });

  useEffect(() => {
    // Skip if disabled
    if (options?.enabled === false) {
      return;
    }

    // Skip if channel is empty or undefined
    if (!channel) {
      return;
    }

    // Skip if not connected
    if (!isConnected) {
      return;
    }

    // Subscribe
    const subscriptionId = listen(channel, event as string, (data: unknown) => {
      callbackRef.current(data as WebSocketEventMap[T]);
    });

    subscriptionIdRef.current = subscriptionId;

    // Cleanup
    return () => {
      if (subscriptionIdRef.current) {
        stopListening(subscriptionIdRef.current);
        subscriptionIdRef.current = null;
      }
    };
  }, [
    channel,
    event,
    isConnected,
    listen,
    stopListening,
    options?.enabled,
    ...(options?.dependencies || []),
  ]);

  return {
    isConnected,
    subscriptionId: subscriptionIdRef.current,
  };
}

// Hook for channel subscription
export function useWebSocketChannel(
  channel: string,
  options?: {
    type?: 'public' | 'private' | 'presence';
    autoSubscribe?: boolean;
  }
) {
  const subscribe = useSubscriptionStore((state) => state.subscribe);
  const unsubscribe = useSubscriptionStore((state) => state.unsubscribe);
  const isSubscribed = useSubscriptionStore((state) => state.isSubscribed);
  const getChannel = useSubscriptionStore((state) => state.getChannel);
  const isConnected = useConnectionStore((state) => state.isConnected);

  const channelInfo = getChannel(channel);
  const subscribed = isSubscribed(channel);

  useEffect(() => {
    if (options?.autoSubscribe !== false && isConnected && !subscribed) {
      subscribe(channel, options?.type);
    }

    return () => {
      if (options?.autoSubscribe !== false && subscribed) {
        unsubscribe(channel);
      }
    };
  }, [
    channel,
    isConnected,
    subscribed,
    subscribe,
    unsubscribe,
    options?.type,
    options?.autoSubscribe,
  ]);

  return {
    subscribed,
    channelInfo,
    subscribe: useCallback(
      () => subscribe(channel, options?.type),
      [channel, subscribe, options?.type]
    ),
    unsubscribe: useCallback(
      () => unsubscribe(channel),
      [channel, unsubscribe]
    ),
  };
}

// Hook for connection status
export function useWebSocketStatus() {
  const status = useConnectionStore((state) => state.status);
  const isConnected = useConnectionStore((state) => state.isConnected);
  const lastError = useConnectionStore((state) => state.lastError);
  const forceReconnect = useConnectionStore((state) => state.forceReconnect);

  return {
    status,
    isConnected,
    lastError,
    forceReconnect,
  };
}

// Hook for WebSocket metrics
export function useWebSocketMetrics() {
  const totalMessages = useMetricsStore((state) => state.metrics.totalMessages);
  const messagesPerMinute = useMetricsStore(
    (state) => state.metrics.messagesPerMinute
  );
  const reconnectCount = useMetricsStore(
    (state) => state.metrics.reconnectCount
  );
  const connectionUptime = useConnectionStore((state) =>
    state.getConnectionUptime()
  );
  const activeChannels = useSubscriptionStore(
    (state) => state.getActiveChannels().length
  );
  const subscriptionCount = useSubscriptionStore((state) =>
    state.getSubscriptionCount()
  );

  return {
    totalMessages,
    messagesPerMinute,
    connectionUptime,
    reconnectCount,
    activeChannels,
    subscriptionCount,
  };
}

// Hook for message history
export function useWebSocketMessages(limit = 100) {
  const getRecentMessages = useMetricsStore((state) => state.getRecentMessages);
  return getRecentMessages(limit);
}

// Hook for global message handler
export function useWebSocketGlobalHandler(
  handler: (message: WebSocketMessage) => void,
  dependencies: unknown[] = []
) {
  const addGlobalHandler = useMetricsStore((state) => state.addGlobalHandler);

  const handlerRef = useRef(handler);

  // Update handler ref when it changes
  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    const unsubscribe = addGlobalHandler((message: WebSocketMessage) => {
      handlerRef.current(message);
    });

    return unsubscribe;
  }, [addGlobalHandler, ...dependencies]);
}

// Hook for private channel subscription
export function usePrivateChannel(channelName: string) {
  const isConnected = useConnectionStore((state) => state.isConnected);
  const subscribe = useSubscriptionStore((state) => state.subscribe);
  const unsubscribe = useSubscriptionStore((state) => state.unsubscribe);
  const getChannel = useSubscriptionStore((state) => state.getChannel);

  useEffect(() => {
    if (!isConnected || !channelName) return;

    subscribe(channelName, 'private');

    return () => {
      unsubscribe(channelName);
    };
  }, [channelName, isConnected, subscribe, unsubscribe]);

  return getChannel(channelName);
}
